import { useCallback, useState } from 'react';
import { useToggler } from '@cbhq/cds-common/hooks/useToggler';
import { useAlert } from '@cbhq/cds-common/overlays/useAlert';
import { CellSpacing } from '@cbhq/cds-web';
import { Card } from '@cbhq/cds-web/cards/Card';
import { ListCell } from '@cbhq/cds-web/cells/ListCell';
import { Checkbox, SearchInput, Select, SelectOption } from '@cbhq/cds-web/controls';
import { Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import { Alert } from '@cbhq/cds-web/overlays';
import { TextBody, TextHeadline, TextLabel2, TextTitle1 } from '@cbhq/cds-web/typography';

import { useGetOrganizations } from ':cloud/queries/PrivilegedAdminQueries/useGetOrganizations';
import { useUpdateProtocolLimits } from ':cloud/queries/PrivilegedAdminQueries/useUpdateProtocolLimits';
import { useGetResourceLimits } from ':cloud/queries/ResourceQueries/useGetResourceLimits';
import { AdminOrganization, IClusterLimits, ProtocolKey } from ':cloud/types/ts_types';
import { threeColumnGrid } from ':cloud/utils/responsiveConfigs';
import { Layout } from ':cloud/widgets/layout';
import { LoadingSpinner, ProtocolIcon } from ':cloud/widgets/sharedcomponents';

const listOuterSpacing: CellSpacing = { spacingVertical: 0 };

interface ProtocolStatusCardProps extends Omit<IClusterLimits, 'userId'> {
  onHandleUpdateLimit: (resourceType: ProtocolKey, newLimit: number, clusterCount: number) => void;
}

function ProtocolStatusCard({
  resourceType,
  clusterCount,
  clusterLimit,
  onHandleUpdateLimit,
}: ProtocolStatusCardProps) {
  const MAX_RESOURCE_LIMIT = 150;
  const numbersArray = Array.from({ length: MAX_RESOURCE_LIMIT }, (_, index) => index.toString());

  const handleChange = useCallback(
    (newLimit) => onHandleUpdateLimit(resourceType, parseInt(newLimit), clusterCount),
    [clusterCount, onHandleUpdateLimit, resourceType],
  );

  return (
    <Card elevation={2} background height={80}>
      <HStack gap={10}>
        <HStack width={200} gap={2} alignItems="center">
          <ProtocolIcon protocolKey={resourceType} size={32} />
          <TextBody as="p" color="foregroundMuted">
            {resourceType?.replace(/qt/g, ' QT')}
          </TextBody>
        </HStack>
        <HStack gap={4}>
          <div>
            <TextLabel2 as="p" color="foregroundMuted">
              Active
            </TextLabel2>
            <TextTitle1 spacingTop={1} as="p">
              {clusterCount || '0'}
            </TextTitle1>
          </div>
          <div>
            <TextLabel2 as="p" color="foregroundMuted">
              Limit
            </TextLabel2>
            <Select value={clusterLimit.toString()} onChange={handleChange}>
              {numbersArray.map((option) => (
                <SelectOption value={option} key={option} title={option} />
              ))}
            </Select>
          </div>
        </HStack>
      </HStack>
    </Card>
  );
}

export function AdminOrg() {
  const [searchQuery, setSearchQuery] = useState<string>();
  const [filteredList, setFilteredList] = useState<AdminOrganization[]>();
  const { organizations, isLoading: isLoadingOrgs } = useGetOrganizations();
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const { resourceLimits, isLoading: isLimitsLoading } = useGetResourceLimits(selectedOrgId);

  const limitMutation = useUpdateProtocolLimits();
  const [showOnlyProtocolsWithLimits, { toggle: toggleProtocolWithLimits }] = useToggler();
  /** TODO: (arti.villa) Add back convert invoice to billing feature */
  // const [isInvoiced, { toggle: toggleSetInvoiced }] = useToggler();
  const downgradeLimitNotifier = useAlert();

  const handleAlertClose = useCallback(() => {
    downgradeLimitNotifier.close();
  }, [downgradeLimitNotifier]);

  const updateLimit = (protocolKey: ProtocolKey, newLimit: number) => {
    limitMutation.mutate(
      { orgId: selectedOrgId, protocolKey, newLimit },
      {
        onSuccess: () => {
          downgradeLimitNotifier.close();
        },
      },
    );
  };

  const onHandleConditionallyUpdateLimit = async (
    protocolKey: ProtocolKey,
    newLimit: number,
    clusterCount: number,
  ): Promise<void> => {
    /** When CX downgrades customers limits that are still greater than running clusters */
    const safeDowngrade = newLimit > clusterCount || newLimit === clusterCount;
    if (safeDowngrade) {
      updateLimit(protocolKey, newLimit);
    } else {
      downgradeLimitNotifier.open(
        <Alert
          visible
          title="Are you sure?"
          body="The number of active clusters running is greater than the limit specified. Proceed anyway? This will prevent the customer from creating more."
          pictogram="warning"
          onRequestClose={handleAlertClose}
          preferredActionLabel="Yes, downgrade"
          onPreferredActionPress={(): void => {
            updateLimit(protocolKey, newLimit);
          }}
          dismissActionLabel="Nevermind"
          onDismissActionPress={() => downgradeLimitNotifier.close}
        />,
      );
    }
  };

  function customerProtocolsSetToZero(item: IClusterLimits): IClusterLimits | null {
    if (showOnlyProtocolsWithLimits) {
      return item.clusterLimit === 0 ? null : item;
    }
    return item;
  }

  const handleFormatList = useCallback(
    (query) => {
      setSearchQuery(query);
      const list = organizations?.filter(
        (item) => item.name.includes(query) || item.id.includes(query),
      );
      setFilteredList(list);
    },
    [organizations],
  );

  const onHandleSelectItem = (item: AdminOrganization) => {
    setSelectedOrgId(item.id);
    setSearchQuery(item?.name || item.id);
    setFilteredList(undefined);
  };

  const handleClear = useCallback(() => {
    setSelectedOrgId('');
    setSearchQuery(undefined);
  }, [setSearchQuery, setSelectedOrgId]);

  // function onHandleSetBillingMethodType() {
  // TODO: (arti.villa) Add back convert invoice to billing feature
  // }

  if (isLoadingOrgs) {
    return <LoadingSpinner />;
  }

  return (
    <Layout>
      <Layout.MainContainer fullWidth>
        <VStack width="100%" spacingBottom={10} gap={10}>
          <SearchInput
            value={searchQuery || ''}
            onChangeText={handleFormatList}
            onClear={handleClear}
          />
          <div>
            {searchQuery &&
              filteredList?.map((item: AdminOrganization) => (
                <ListCell
                  outerSpacing={listOuterSpacing}
                  as="li"
                  key={item.id}
                  title={item.name}
                  description={item.id}
                  onPress={() => onHandleSelectItem(item)}
                />
              ))}
          </div>
          {!isLimitsLoading && selectedOrgId && (
            <Grid responsiveConfig={threeColumnGrid} gap={3}>
              <VStack width={400} gap={2}>
                <TextHeadline as="p">Options</TextHeadline>
                {/* // TODO: (arti.villa) Add back convert invoice to billing feature
                    <Checkbox onChange={onHandleSetBillingMethodType} checked={isInvoiced}>
                      Customer is billed through invoicing
                    </Checkbox> */}
                <Checkbox onChange={toggleProtocolWithLimits} checked={showOnlyProtocolsWithLimits}>
                  Hide protocols with cluster limit of 0
                </Checkbox>
              </VStack>
              <Grid>
                {Object.values(resourceLimits)
                  .filter(customerProtocolsSetToZero)
                  .map(({ resourceType, clusterCount, clusterLimit }) => (
                    <Grid key={resourceType} responsiveConfig={threeColumnGrid}>
                      <ProtocolStatusCard
                        resourceType={resourceType}
                        clusterCount={clusterCount}
                        clusterLimit={clusterLimit}
                        onHandleUpdateLimit={onHandleConditionallyUpdateLimit}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Grid>
          )}
        </VStack>
      </Layout.MainContainer>
    </Layout>
  );
}
